// Partner visits API (2026-10-06): see docs/superpowers/specs/2026-10-06-partner-visits-api-design.md

import { h, Component } from 'preact';

// Bundle marker — bump this string to force webpack to emit a new bundle hash and
// invalidate the service-worker precache (paired with sw.js CACHE_VERSION).
// Assigned to window so terser can't tree-shake it away in production.
if (typeof window !== 'undefined') {
    window.__BUNDLE_VERSION = 'v2-2026-05-03';
}

import { Router, route } from 'preact-router';
import { createHashHistory } from 'history';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer } from 'preact-i18n';
import linkState from 'linkstate';
import AsyncRoute from 'preact-async-route';
import Match from 'preact-router/match';
import { Suspense, lazy } from 'preact/compat';

import util from 'preact-util';
import localUtil from './lib/util';
import { isDarkmodeForced } from './lib/darkmode';
import { applyReduceMotion, REDUCE_MOTION_QUERY } from './lib/reduceMotion';
import { watchContrastPreference } from './lib/contrastPreference';
import './components/dyrejournal/highContrast.css';
import './components/dyrejournal/mediumContrast.css';
import { syncNativeStatusBar, watchNativeStatusBar, isNativeAppPage } from './lib/nativeStatusBar';
import { installNativeDeepLinkBridge } from './lib/nativeDeepLink';
import { rememberNativeRoute, getRememberedNativeRoute } from './lib/nativeApp';
import { installDialogLifecycle } from './lib/dialogLifecycle';
import { isDeepLinkToRemember } from './lib/loginRedirect';
import dialogStore from './stores/dialog';
import DialogHost from './components/dialogs';
import PubSub, { topics } from './lib/pubsub';
import { serializeCaughtError, getOpenDrawerComponents } from './lib/crashReport';
import firebaseSync from './firebaseSync';
import { installFetchApiInterceptor, getVisibilityRefreshPromise } from './lib/fetchApiInterceptor';
import offlineRetryQueue from './lib/offlineRetryQueue';
import { installOfflineCache, clearOfflineCache } from './lib/offlineCache';
import { runPrefetch as runOfflinePrefetch, lastPrefetchAt as lastOfflinePrefetchAt } from './lib/offlinePrefetch';
import { drainQueues } from './lib/offlineDrain';
import { subscribeUserSignal } from './lib/inboxRealtime';
import { deliverUsageLogSnapshot } from './lib/usageLogDelivery';

// Install the fetch API interceptor for transparent token refresh
installFetchApiInterceptor();
// Install the offline read cache. Must run AFTER installFetchApiInterceptor
// so the cache layer sits above the token-refresh layer. See
// docs/superpowers/specs/2026-05-06-offline-read-cache-design.md.
installOfflineCache({
    util,
    getUserId: () => {
        try {
            // Lazy-required to avoid circular imports at module-eval time.
            const userStore = require('./stores/user').default;
            return userStore && userStore.user && userStore.user.id != null
                ? userStore.user.id : null;
        } catch (e) { return null; }
    },
    getCustomerId: () => {
        try {
            const appState = require('./stores/appState').default;
            return appState && appState.currentCustomer && appState.currentCustomer.id != null
                ? appState.currentCustomer.id : null;
        } catch (e) { return null; }
    },
});

// Auto-on-boot offline prefetch. If the last prefetch was >24h ago AND we're
// online AND this customer has explicitly enabled the feature, kick off a
// silent prefetch 5s after boot (so it doesn't compete with the normal
// initial fetches). Default-OFF — admins must flip
// featureFlags.enableOfflinePrefetch on per customer to opt in.
// See docs/superpowers/specs/2026-05-06-offline-prefetch-design.md.
const STALE_PREFETCH_MS = 24 * 60 * 60 * 1000;
setTimeout(() => {
    try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        const last = lastOfflinePrefetchAt();
        if (last != null && Date.now() - last < STALE_PREFETCH_MS) return;
        // Lazy-required to avoid circular imports at module-eval time.
        const appState = require('./stores/appState').default;
        const flags = appState && appState.currentCustomer && appState.currentCustomer.featureFlags;
        if (!flags || !flags.enableOfflinePrefetch) return;
        runOfflinePrefetch({ silent: true });
    } catch (e) {
        // Auto-prefetch is best-effort; never let it break boot.
    }
}, 5000);
import versionData from './version.json';

const SCROLLDOWN_LIMIT = 250;
const MENU_HIDE_SCROLLDOWN = 15;
const history = createHashHistory();

// import Start from './routes/start';

import RefreshSpinner from './components/refresh';
import ReadyToRefresh from './components/refresh/ready';
import Progress from './components/progress';
import ChristmasLights from './components/gui/christmasLights';
import HalloweenDecoration from './components/gui/halloweenDecoration';

// Product store registries — merged into this.stores below. Tilleggsmodul (C)
// stores live ONLY in stores-other, signing/NDA (B✕) stores ONLY in
// stores-signing, and both must be accessed guarded
// (`otherStores.x?.method()` / `signingStores.x?.method()`): the
// Dyrejournal-repo export replaces those files with empty stubs.
// See docs/dyrejournal-separation/registry-architecture.md.
import coreStores from './product/stores-core';
import dyrejournalStores from './product/stores-dyrejournal';
import otherStores from './product/stores-other';
import signingStores from './product/stores-signing';
import otherRoutes, { otherPublicRoutes } from './product/routes-other';
import signingRoutes, { signingPublicRoutes } from './product/routes-signing';
import componentsOther from './product/components-other';

// Stores referenced directly by App's own logic (core + dyrejournal only —
// present in every product build; same singletons as in the registries).
import appState from './stores/appState';
import userStore from './stores/user';
import customerStore from './stores/customer';
import mediaStore from './stores/media';
import usageStore from './stores/usage';
import saleStore from './stores/sale';
import salePaymentStore from './stores/salePayment';
import saleReportStore from './stores/saleReport';
import productStore from './stores/product';
import fileStore from './stores/file';
import contactStore from './stores/contact';
import dealerStore from './stores/dealer';
import pricelistStore from './stores/pricelist';
import calendarStore from './stores/calendar';
import calendarEventStore from './stores/calendarEvent';
import calendarAvailabilityStore from './stores/calendarAvailability';
import calendarTemplateStore from './stores/calendarTemplate';
import textTemplateStore from './stores/textTemplate';
import smsStore from './stores/sms';
import mailSentStore from './stores/mailSent';
import messageStore from './stores/message';
import supportTicketStore from './stores/supportTicket';
import fikenStore from './stores/fiken';
import articleStore from './stores/article';
import journalStore from './stores/journal';
import prescriptionStore from './stores/prescription';
import drugStore from './stores/drug';
import animalStore from './stores/animal';
import animalSpecieStore from './stores/animalSpecie';
import animalBreedStore from './stores/animalBreed';
import visitorStore from './stores/visitor';
import diagnoseStore from './stores/diagnose';

import definitionNo from './languages/no.json';
import definitionEn from './languages/en.json';

import LoadingSpinner from './components/loadingSpinner/';

import Header from './components/header';
import LockScreen from './components/lockScreen';
import lockScreenStore from './stores/lockScreen';
import Sidebar from './components/sidebar';
import Footer from './components/footer';
import Offline from './components/offline';
import { use } from 'react';
import GdprBanner from './components/gui/gdprBanner';
import { hasConsent, onConsentChange } from './components/gui/gdprBanner';
import QuickSearch from './components/quickSearch';
import SessionWarningBanner from './components/gui/sessionWarningBanner';

function getBrowserInfo() {
    const userAgent = navigator.userAgent;
    const language = navigator.language;

    // Basic parsing of userAgent to identify browser and OS - this can be expanded
    let browserName = userAgent.match(/firefox|fxios/i) ? 'Firefox' :
                      userAgent.match(/chrome|chromium|crios/i) ? 'Chrome' :
                      userAgent.match(/safari/i) ? 'Safari' :
                      userAgent.match(/opr\//i) ? 'Opera' :
                      userAgent.match(/edg/i) ? 'Edge' :
                      'Other';

    let osName = userAgent.match(/android/i) ? 'Android' :
                 userAgent.match(/iphone|ipad|ipod/i) ? 'iOS' :
                 userAgent.match(/win/i) ? 'Windows' :
                 userAgent.match(/mac/i) ? 'MacOS' :
                 userAgent.match(/linux/i) ? 'Linux' :
                 'Other';

    return {
        userAgent: userAgent,
        browser: browserName,
        os: osName,
        language: language
    };
}

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

const refreshRange = 150;
const readyToRefreshRange = 50;
const maxRange = 350;
const actionRange = 250;
const indicateActionRange = 50;

class Catcher extends Component {
    constructor() {
        super();
        this.state = {
            errored: false,
            errorsReported: 0,
        };
    }

    componentDidCatch(error) {
        const { errorsReported } = this.state;
        if (errorsReported < 10) {
            const data = {
                timestamp: Math.floor(new Date().getTime() / 1000),
                // Drawers ride inside `error` — POST /api/errors/ only accepts
                // the error/location/browser/timestamp fields.
                error: {
                    ...serializeCaughtError(error),
                    drawers: getOpenDrawerComponents(appState),
                },
                location: window.location,
                browser: getBrowserInfo(),
            };
            this.setState({ errored: true, errorsReported: errorsReported + 1 });
            util.fetchApi('/api/errors/', { method: 'POST', publish: false }, data);
        }
    }

    reload = (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign('/');
        // window.location.reload();
    }

    render(props, state) {
        if (state.errored) {
            return (
                <div class='container-fluid h-100'>
                    <div class='row h-100'>
                        <div class='col-12 text-center my-auto'>
                            <div class='display-1 text-muted'>
                                <i class='fa-duotone fa-bug' />
                            </div>
                            <h5>Uuuh, this was not supposed to happen...</h5>
                            <p>Error is automatically reported.</p>
                            <a class='mt-3 btn btn-lg btn-primary' href='#' onClick={this.reload}>Reload the app!</a>
                        </div>
                    </div>
                </div>
            );
        }
        return props.children;
    }
}

@observer
class App extends Component {
	constructor(props) {
        super(props);
		this.stores = {
            ...coreStores,
            ...dyrejournalStores,
            ...otherStores,
            ...signingStores,
            history,
        };
        const startUrl = window.location.hash.replace(/^#/, '');
        this.state = {
            jwtToken: util.getJwtToken(),
            refreshTime: new Date().getTime(),
            currentUrl: startUrl,
            checkCordovaCounter: 0,
            bug: {},
            inputProps: {},
            online: true,
            refreshing: false,
            sessionWarning: false,
        };
        this.logMessages = [],
        this.subscriptions();
        const { user, isAdmin, saved } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const tightmode = util.getNestedValue(user, 'settings.tightmode');
        const hue = util.getNestedValue(user, 'settings.hue');
        localUtil.toggleBootstrap53DarkMode(darkmode, hue);
        syncNativeStatusBar({ darkmode: !!darkmode });
        applyReduceMotion(user, { matchMedia: window.matchMedia?.bind(window), toggleBodyClasses: util.toggleBodyClasses });
        // util.toggleBodyClasses('tight-mode', tightmode);
        this.appContainer = null;
        this.quickSearchRef = null;
    }

    overrideConsole() {
        const oldConsoleLog = console.log;
        const oldConsoleError = console.error;

        // console.log = (...args) => {
        //     this.logMessages.push({
        //         type: 'log',
        //         message: args,
        //         timestamp: Math.floor(new Date().getTime() / 1000),
        //         browser: getBrowserInfo(),
        //     });
        //     oldConsoleLog.apply(console, args);
        // };

        console.error = (...args) => {
            this.logMessages.push({
                type: 'error',
                message: args,
                timestamp: Math.floor(new Date().getTime() / 1000),
                browser: getBrowserInfo(),
            });
            oldConsoleError.apply(console, args);
        };
    }

    sendLogs = async () => {
        if (this.sendLogsInProgress) {
            return;
        }
        this.sendLogsInProgress = true;
        try {
            if (this.logMessages.length > 0) {
                const errorSnapshot = this.logMessages.slice();
                try {
                    const errorResponse = await util.fetchApi(
                        '/api/errors/',
                        { method: 'POST', publish: false },
                        { errors: errorSnapshot },
                    );
                    if ([200, 201].includes(errorResponse?.status)) {
                        this.logMessages.splice(0, errorSnapshot.length);
                    }
                } catch (error) {
                    // Error reporting and usage delivery retry independently.
                }
            }
            await deliverUsageLogSnapshot({
                usageLog: appState.usageLog,
                fetchApi: util.fetchApi,
                onAccepted: length => appState.removeUsageLogPrefix(length),
            });
        } catch (err) {
            // Retain both queues for the next timer attempt.
        } finally {
            this.sendLogsInProgress = false;
        }
    }

    startLogTimer = () => {
        appState.setUsageLogFlusher(this.sendLogs);
        clearInterval(this.sendLogsTimer);
        this.sendLogsTimer = setInterval(() => {
            this.sendLogs();
        }, 10000); // Every 10 seconds
    }

    postLog = async () => {
        const logs = [
            ...(articleStore.getLogs() || []),
            ...userStore.getLogs(),
            ...(otherStores.newsStore?.getLogs() || []),
            ...mediaStore.getLogs(),
            ...customerStore.getLogs(),
            ...(otherStores.installationStore?.getLogs() || []),
            ...(otherStores.partStore?.getLogs() || []),
            ...(otherStores.inspectionStore?.getLogs() || []),
            ...(otherStores.loanStore?.getLogs() || []),
            ...usageStore.getLogs(),
            ...(otherStores.raceStore?.getLogs() || []),
            ...(otherStores.raceClassStore?.getLogs() || []),
            ...(otherStores.raceClassContestantStore?.getLogs() || []),
            ...(otherStores.raceResultStore?.getLogs() || []),

            ...journalStore.getLogs(),
            ...prescriptionStore.getLogs(),
            ...drugStore.getLogs(),
            ...animalStore.getLogs(),
            ...animalSpecieStore.getLogs(),
            ...animalBreedStore.getLogs(),
            ...visitorStore.getLogs(),
            ...saleStore.getLogs(),
            ...salePaymentStore.getLogs(),
            ...saleReportStore.getLogs(),
            ...productStore.getLogs(),
            ...diagnoseStore.getLogs(),
            ...fileStore.getLogs(),
            ...(otherStores.gpsPointStore?.getLogs() || []),
            ...(otherStores.gpsDeviceStore?.getLogs() || []),
            ...(otherStores.streamStore?.getLogs() || []),
            ...(otherStores.streamDeviceStore?.getLogs() || []),

            ...(otherStores.conventionStore?.getLogs() || []),
            ...(otherStores.conventionResultStore?.getLogs() || []),
            ...(otherStores.competitionResultStore?.getLogs() || []),

            ...(otherStores.timeTrackerStore?.getLogs() || []),

            ...contactStore.getLogs(),
            ...dealerStore.getLogs(),
            ...pricelistStore.getLogs(),

            ...calendarStore.getLogs(),
            ...calendarEventStore.getLogs(),
            ...calendarAvailabilityStore.getLogs(),
            ...textTemplateStore.getLogs(),
            ...calendarTemplateStore.getLogs(),
            ...smsStore.getLogs(),
            ...mailSentStore.getLogs(),
        ];
        if (logs.length > 0) {
            userStore.postLog({ data: logs });
        }
    }

    subscriptions() {
        PubSub.subscribe(topics.LOG_OUT, () => {
            dialogStore.cancelAll();
            lockScreenStore.clearOnLogout();
            // console.log('LOG_OUT');
            firebaseSync.stop();
            // Capture the user id BEFORE any state cleanup so we can scope the
            // offline-cache eviction below. Bare access guarded by optional
            // chaining — userStore.user is always at least an empty object.
            const userIdForCacheEviction = userStore && userStore.user && userStore.user.id != null
                ? userStore.user.id : null;
            // Mark the session as terminated so the fetch interceptor refuses
            // any in-flight or scheduled refreshes, and so the migrationTokens
            // path in the interceptor doesn't re-authenticate the user from a
            // stale cached /api/users/info response.
            window.__loggingOut = true;
            // Revoke the server-side session and clear the HttpOnly cookie
            // backup, so the silent cookie-based recovery can't resurrect a
            // session the user explicitly ended. Fire-and-forget: logout must
            // never block on the network.
            const refreshTokenForRevoke = localStorage.getItem('refreshToken');
            fetch(`${util.getApiServer()}/api/sessions/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(refreshTokenForRevoke ? { refreshToken: refreshTokenForRevoke } : {}),
            }).catch(() => {});
            this.setState({
                jwtToken: undefined,
            }, async () => {
                util.removeUserEmail();
                util.removeUserCellphone();
                localStorage.removeItem('refreshToken');
                util.setJwtToken('');
                util.removeJwtToken();
                appState.toggleDarkmode(false);
                // Purge any service-worker caches that could hold authenticated
                // responses (esp. /api/users/info). Without this the next page
                // load serves a stale 200 with the old user — and if that cached
                // response carries migrationTokens, the interceptor silently
                // re-logs the user in. Image and HTML caches are left alone.
                if (typeof caches !== 'undefined') {
                    try {
                        const cacheNames = await caches.keys();
                        await Promise.all(
                            cacheNames
                                .filter(n => /^appapi/.test(n) || n === 'mainhtml')
                                .map(n => caches.delete(n))
                        );
                    } catch (err) {
                        console.log('LOG_OUT: cache purge err', err);
                    }
                }
                // Clear this user's offline-cache entries from IndexedDB.
                // Skip if we couldn't capture an id at the start of the
                // handler — passing null would match the 'anon' bucket and
                // wipe entries belonging to logged-out sessions on shared
                // devices. clearOfflineCache is best-effort; it swallows
                // its own errors.
                if (userIdForCacheEviction != null) {
                    await clearOfflineCache({ userId: userIdForCacheEviction });
                }
                setTimeout(() => {
                    route('/');
                    window.location.reload(true);
                }, 200);
            });
        });

        PubSub.subscribe(topics.JWT_TOKEN_CHANGED, async (token) => {
            this.setState({
                jwtToken: token,
            }, () => {
                // window.location.reload(true);
                this.enforcePinSetupGate();
            });
        });

        PubSub.subscribe(topics.LOCK_SCREEN, () => {
            lockScreenStore.lock();
        });

        PubSub.subscribe(topics.SESSION_WARNING, () => {
            this.setState({ sessionWarning: true });
        });

        PubSub.subscribe(topics.SESSION_RECONNECTED, () => {
            this.setState({ sessionWarning: false });
        });
    }

    redirectIfNotLoggedIn = (e) => {
        const { opts } = appState;
        if (opts.skipRedirect) {
            return false;
        }
        const { jwtToken } = this.state;
        // Also check for refresh token — if it exists, the interceptor can still
        // recover the session. Only redirect to /welcome if BOTH are missing.
        const hasRefreshToken = !!localStorage.getItem('refreshToken');
        if (!jwtToken && !hasRefreshToken && !/^\/(welcome|login|register|forgotten)/.test(e.url)) {
            // Remember the deep link (newsletter, SMS, shared article) so the login flows
            // route back to it instead of the front page. routes/login/*.js read and clear it.
            if (isDeepLinkToRemember(e.url)) {
                appState.setWantedPage(e.url);
            }
            return route('/welcome');
        }
    }

    redirectIfNotApproved = (e) => {
        const { opts } = appState;
        if (opts.skipRedirect) {
            return false;
        }
        const { user, isAdmin } = userStore;
        if (isAdmin) {
            return;
        }
        const isUserApproved = user && user.id && user.isApproved;
        if (!isUserApproved) {
            return route('/waiting-approval');
        }
        const { currentUrl } = this.state;
        if (/^\/(waiting-approval)/.test(currentUrl)) {
            return route('/');
        }
    }

    /** Gets fired when the route changes.
	 *	@param {Object} event		"change" event from [preact-router](http://git.io/preact-router)
	 *	@param {string} event.url	The newly routed URL
	 */
	updatePageTitle = (url) => {
		const { currentCustomer } = appState;
		// const baseTitle = currentCustomer?.name || 'Keepspot';
        // As the baseTitle I want to use domain name.
        const baseTitle = window.location.hostname;
		const titleMap = {
            // '/': 'Frontpage',
			// Dyrejournal
			'/dyrejournal-sale': 'Besøk',
			'/dyrejournal-mobile': 'Mobil Journal',
			'/dyrejournal-clinic': 'Klinikk',
			'/dyrejournal-lab-results': 'Lab resultater',
			'/dyrejournal-idexx-mapping': 'IDEXX-mapping',
			'/dyrejournal-payments': 'Betalinger',
			'/dyrejournal-sms': 'SMS',
			'/dyrejournal-sms-reminder-log': 'SMS Reminder Log',
			'/dyrejournal-no-shows': 'Ikke møtt',
			'/dyrejournal-mailsent': 'E-post',
			'/dyrejournal-files': 'Filer',
			'/dyrejournal-pending-sales': 'Ventende salg',
			'/dyrejournal-contacts': 'Kontakter',
			'/dyrejournal-phone-consultations': 'Telefonkonsultasjoner',
			'/dyrejournal-prescription-orders': 'Reseptbestillinger',
			'/dyrejournal-admin': 'Kunde Admin',
			'/pistolklubb-admin': 'Klubb Admin',
			// Calendar
			'/calendar/notifications': 'Varsler',
			'/calendar': 'Kalender',
			'/calendarBooking': 'Timebestilling',
			'/prescriptionOrder': 'Reseptbestilling',
			// Support
			'/support/staff': 'Support',
			'/support/new': 'Ny henvendelse',
			'/support/refund': 'Refund Request',
			'/support/ticket': 'Sak',
			'/support': 'Support',
			'/work-sharing': 'Arbeidsdeling',
			// User
			'/users/profile/inbox': 'Innboks',
			'/users/profile': 'Min profil',
			// Admin
			'/admin/development': 'Admin: Utvikling',
			'/admin/content-curation': 'Admin: Innhold',
			'/admin/imports': 'Admin: Import',
			'/admin/user': 'Admin: Brukere',
			'/admin/customer': 'Admin: Kunder',
			'/admin': 'Admin',
			'/errors': 'Feillogg',
			'/statistics': 'Statistikk',
			'/apilogger': 'API-logg',
			'/querylogger': 'Spørringslogg',
			'/admin-sales': 'Admin: Salg',
			'/admin-raceClassContestants': 'Admin: Race Contestants',
			// Products
			'/products': 'Produkter',
			'/cart': 'Handlekurv',
			'/checkout': 'Betaling',
			// Other
			'/help': 'Hjelp',
			'/news': 'Nyheter',
			'/texttemplates': 'Tekstmaler',
			'/visitor': 'Kunder',
			'/animal': 'Dyr',
			'/race': 'Løp',
			'/course': 'Kurs',
			'/exam': 'Prøve',
			'/elearning': 'E-læring',
			'/login': 'Logg inn',
			'/register': 'Registrer',
			'/welcome': 'Velkommen',
            '/onBoarding/': 'Onboarding',
            '/onBoardingClub/': 'Startinfo klubb',
		};

		// Find matching route (longest match first)
		const sortedRoutes = Object.keys(titleMap).sort((a, b) => b.length - a.length);
		const matchedRoute = sortedRoutes.find(route => url.startsWith(route));

		if (matchedRoute) {
			document.title = `${titleMap[matchedRoute]} | ${baseTitle}`;
		} else {
			document.title = baseTitle;
		}
	}

	handleRoute = e => {
        const { scrolledDown } = this.state;
        const { prevScroll, opts } = appState;
        const { user, isAdmin } = userStore;

		// Update browser tab title
		this.updatePageTitle(e.url);

        try {
            userStore.onPageView(e.url);
        } catch (err) {
            console.error('Error sending page_view event:', err);
        }

        appState.addUsageLog({
            type: 'route',
            url: e.url,
            prevUrl: e.previous,
            timestamp: Math.floor(new Date().getTime() / 1000),
            browser: getBrowserInfo(),
            user: user ? user.id : null,
            isAdmin,
        });

        this.redirectIfNotLoggedIn(e);
        // console.log('handleRoute', e.url, jwtToken, !/^\/(welcome|login|register|forgotten)/.test(e.url), (!jwtToken && !/^\/(welcome|login|register|forgotten)/.test(e.url)))
        // if (!jwtToken && !/^\/(welcome|login|register|forgotten)/.test(e.url)) {
        //     route('/welcome');
        // }
        if (/^\/register/.test(e.previous)) {
            this.redirectIfNotApproved();
        }
		this.setState({ currentUrl: e.url });
        appState.setPrevPath(e.previous);
        appState.setPrevScroll(scrolledDown);
        appState.setPath(e.url);
        // Inside the store apps: remember where we are so a cold launch after iOS
        // killed the app can route back here (see lib/nativeApp.js). No-op on the web.
        rememberNativeRoute(e.url);
        this.cleanupMemory(e.url, e.previous);
        const { router } = e;
        const { props = {} } = router;
        const { history = {} } = props;
        const { action = {} } = history;
        if (this.appContainer) {
            // if (prevScroll && action === 'POP') {
            //     console.log({ e, scrolledDown, prevScroll }, this.appContainer);
            //     this.scrollTimer = setTimeout(() => this.appContainer.scrollTo({ top: prevScroll }), 2000);
            // }
            if (!e.url.match(/skipScroll/)) {
                // this.appContainer.scrollTo({ top: 0 });
                localUtil.scrollTo(this.appContainer);
            }
        }
        // switch (e.url) {
        //     case '/profile':
        //         const isAuthed = await this.isAuthenticated();
        //         if (!isAuthed) route('/', true);
        //         break;
        // }
	}

    cleanupMemory = (currentUrl, prevUrl) => {
        try {
            const urlRegexpWorkout = new RegExp(`^/workouts`);
            if (urlRegexpWorkout.test(prevUrl) && !urlRegexpWorkout.test(currentUrl)) {
                // workoutStore.cleanupMemory();
            }
        } catch (err) {
            console.log(err);
        }
    }

    async loadAll() {
        const { language: appStateLanguage } = appState;

        // const { jwtToken } = this.state;
        // appState.updateField('jwtToken', jwtToken);
        // util.setJwtToken(jwtToken);
        // await appState.getInfo();
        const serverHostname = window.location.hostname;

        await appState.getFingerprint();
        await appState.getCustomerByHostname();

        // Update page title now that customer is loaded (may have been called earlier with fallback)
        this.updatePageTitle(window.location.hash.replace(/^#/, '') || '/');

        const hasGivenAnalyticsConsent = hasConsent('analytics');
        console.log('hasGivenAnalyticsConsent', hasGivenAnalyticsConsent);

        const online = await userStore.getInfo({
            loadAllUsers: false,
            skipSummary: true,
            skipStaticDataLoad: false,
            language: appStateLanguage,
            hostname: serverHostname,
            skipFirebase: hasGivenAnalyticsConsent ? false : true, // Skip Firebase if no analytics consent
        });
        this.setState({ online });

        const ls = userStore.customer?.featureFlags?.lockScreen;
        if (ls && ls.enabled) {
            lockScreenStore.updateConfig({
                featureEnabled: true,
                softLockMinutes: Number(ls.softLockMinutes) || 15,
                hardLogoutHours: Number(ls.hardLogoutHours) || 12,
            });
            lockScreenStore.setupTimers();
            // Re-apply lock if the page was refreshed while locked.
            lockScreenStore.rehydrate();
        }
        this.enforcePinSetupGate();

        const { isAdmin, isRaceAdmin, isVeterinary, user, customer, build, buildDate } = userStore;

        // Silently refresh web push token on load if permission was previously granted
        // (handles FCM token rotation without user interaction)
        if (user && user.id) {
            userStore.checkWebNotificationPermission();
        }

        // Inject customer's custom invoice CSS into document head
        if (customer?.invoiceCustomCss) {
            const existingStyle = document.getElementById('invoice-custom-css');
            if (existingStyle) {
                existingStyle.remove();
            }
            const styleElement = document.createElement('style');
            styleElement.id = 'invoice-custom-css';
            styleElement.textContent = customer.invoiceCustomCss;
            document.head.appendChild(styleElement);
        }

        // Load pending content count for admin users
        if (isAdmin) {
            otherStores.curatedContentStore?.loadPendingCount();
            // Content moderation is an excluded module even though articleStore
            // itself ships (help center) — only poll the moderation count when
            // the /admin/moderation route is present in the C route registry.
            if (otherRoutes.some(route => route.path === '/admin/moderation')) {
                articleStore.loadModerationPendingCount();
            }
        }

        appState.setLatestVersion({
            build,
            date: buildDate,
        });
        appState.setUser(user);

        // Domain-forced dark mode (customer.featureFlags.forceDarkmode): set the
        // user's dark setting in memory only, so every component that reads
        // user.settings.darkmode agrees. Nothing is saved to the user.
        if (isDarkmodeForced(appState.currentCustomer)) {
            userStore.updateSettingLocal({ darkmode: 1 });
        }
        const darkmode = util.getNestedValue(userStore.user, 'settings.darkmode');
        const hue = util.getNestedValue(user, 'settings.hue');
        localUtil.toggleBootstrap53DarkMode(darkmode, hue);
        syncNativeStatusBar({ darkmode: !!darkmode });

        const tightmode = util.getNestedValue(user, 'settings.tightmode');
        util.toggleBodyClasses('tight-mode', tightmode);
        applyReduceMotion(user, { matchMedia: window.matchMedia?.bind(window), toggleBodyClasses: util.toggleBodyClasses });

        const { opts, currentCustomer } = appState;
        this.redirectIfNotApproved();

        if (user && user.id && customer && customer.id) {
            const syncUserName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
            firebaseSync.start({ customerId: customer.id, userId: user.id, userName: syncUserName });
        }

        fikenStore.updateKeyValue('slug', customer?.fikenSlug);

        // If showCustomerLogo is enabled, we need to update the html title, icon and logo
        if (opts.showCustomerLogo && currentCustomer?.id) {
            const { icons, name } = currentCustomer;
            appState.storeDefaultMetaTags({ title: currentCustomer.name });
            // Title is now handled by updatePageTitle() in handleRoute
            if (currentCustomer.icons && currentCustomer.icons.length > 0) {
                const icon = localUtil.displayImg(currentCustomer.icons[0], this.props, '220x');
                const favicon = document.querySelector('link[rel="icon"]');
                favicon.href = icon;
                const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
                appleTouchIcon.href = icon;
            }
            if (currentCustomer.backgroundColor) {
                document.body.style.backgroundColor = currentCustomer.backgroundColor;
                const appDiv = document.querySelector('#app');
                appDiv.classList.remove('bg-light', 'bg-dark');
                appDiv.style.backgroundColor = currentCustomer.backgroundColor;
            }
            if (currentCustomer.logoHeight) {
                try {
                    const appDiv = document.querySelector('#app');
                    // Skip padding for tracking pages (full-screen map)
                    const currentPath = window.location.hash.replace('#', '') || window.location.pathname;
                    const isTrackingPage = /^\/(race\/\d+\/\d+\/tracking|race\/\d+\/tracking)/.test(currentPath);
                    if (!isTrackingPage) {
                        appDiv.style.paddingTop = `${currentCustomer.logoHeight}`;
                    }
                } catch (err) {
                    console.log(err);
                }
            }
        }

        // await userStore.getInfo(false, true, false, 'en', darkmode ? 1 : 0);
        // const { isAdmin, isRaceAdmin, isVeterinary, user } = userStore;

        // Determin version of app
        const hostname = window.location.host;
        // const storedLocation = user.settings?.locationBeta ? 'beta' : 'prod';
        let appVersion;
        switch (hostname) {
            case 'keepspot.app':
                appVersion = 'prod';
                break;
            case 'dev.keepspot.app':
                appVersion = 'beta';
                break;
            case '127.0.0.1:8080':
            case 'localhost:8080':
                appVersion = 'dev';
                break;
            default:
                appVersion = 'prod';
        }

        if (/Android/i.test(navigator.userAgent)) {
            document.body.classList.add('android');
        }

        // this.websocket({
        //     id: 1000,
        //     firstname: 'anonymous',
        //     lastname: 'anonymous',
        // });

        // console.log({ appVersion });
        // if (appVersion === 'prod' && storedLocation === 'beta') {
        //     const location = 'https://dev.keepspot.app';
        //     window.location = location;
        // }
        this.performanceTimer = setInterval(() => this.postLog(), 10 * 1000);

        this.inboxTimer = setInterval(() => {
            messageStore.getUnreadCount();
        }, 60 * 1000);
        messageStore.getUnreadCount();

        this.notificationsTimer = setInterval(() => {
            userStore.getNotifications();
        }, 60 * 1000);
        userStore.getNotifications();
        const inboxUser = userStore.user;
        if (inboxUser && inboxUser.uuidv4) {
            this.inboxSignalUnsub = subscribeUserSignal(inboxUser.uuidv4, () => {
                messageStore.getUnreadCount();
            });
        }

        this.supportTimer = setInterval(() => {
            supportTicketStore.getUnreadCount();
        }, 60 * 1000);
        supportTicketStore.getUnreadCount();

        const supportStaffUser = userStore.user;
        if (userStore.isAdmin || supportStaffUser?.isSupport) {
            this.supportOpenTimer = setInterval(() => {
                supportTicketStore.getOpenCount();
            }, 60 * 1000);
            supportTicketStore.getOpenCount();
        }

        if (signingStores.formSigningRequestStore) {
            this.signingTimer = setInterval(() => {
                signingStores.formSigningRequestStore.getUnreadCount();
            }, 60 * 1000);
            signingStores.formSigningRequestStore.getUnreadCount();
        }
    }

    touchStart = (e) => {
        this.setState({
            startX: e.touches[0].clientX,
            startY: e.touches[0].clientY,
        });
    }

    touchEnd = (e) => {
        const {
            refreshing,
        } = this.state;
        // Animated snap-back: keep swipeDownPx briefly for CSS transition
        this.setState({
            isTouchDragging: false,
            startY: undefined,
            startX: undefined,
            readyToRefresh: false,
            swipeUpPx: 0,
        });
        // Delay resetting swipeDownPx to allow CSS transition
        setTimeout(() => {
            this.setState({
                swipeDownPx: refreshing ? refreshRange : 0,
            });
        }, 50);

        const {
            swipeRight = () => {},
            swipeLeft = () => {},
            swipeUp = () => {},
            swipeDown = () => {},
            swipeRightIndicate = () => {},
            swipeLeftIndicate = () => {},
            swipeUpIndicate = () => {},
            swipeDownIndicate = () => {},
        } = appState;

        swipeRightIndicate(e, { startY: 0, startX: 0, xUp: 0, yUp: 0, xDiff: 0, yDiff: 0, absXDiff: 0, absYDiff: 0 });
        swipeLeftIndicate(e, { startY: 0, startX: 0, xUp: 0, yUp: 0, xDiff: 0, yDiff: 0, absXDiff: 0, absYDiff: 0 });
        swipeUpIndicate(e, { startY: 0, startX: 0, xUp: 0, yUp: 0, xDiff: 0, yDiff: 0, absXDiff: 0, absYDiff: 0 });
        swipeDownIndicate(e, { startY: 0, startX: 0, xUp: 0, yUp: 0, xDiff: 0, yDiff: 0, absXDiff: 0, absYDiff: 0 });
    }

    touchMove = (e) => {
        const { startY, startX, refreshing } = this.state;
        const {
            swipeRight = () => {},
            swipeLeft = () => {},
            swipeUp = () => {},
            swipeDown = () => {},
            swipeRightIndicate = () => {},
            swipeLeftIndicate = () => {},
            swipeUpIndicate = () => {},
            swipeDownIndicate = () => {},
        } = appState;
        this.setState({
            isTouchDragging: true,
        });

        const xUp = e.touches[0].clientX;
        const yUp = e.touches[0].clientY;

        const xDiff = startX - xUp;
        const yDiff = startY - yUp;
        const absXDiff = Math.abs(xDiff);
        const absYDiff = Math.abs(yDiff);
        if ( absXDiff > absYDiff ) { /*most significant*/
            if ( xDiff > 0 ) {
                /* left swipe */
                // console.log('left swipe', { startY, startX, xDiff, swipeLeft });
                // if (absXDiff > actionRange || absXDiff > indicateActionRange) {
                //     e.preventDefault();
                //     e.stopPropagation();
                // }
                if (absXDiff > actionRange) {
                    swipeLeft(e, { startY, startX, xUp, yUp, xDiff, yDiff, absXDiff, absYDiff });
                }
                if (absXDiff > indicateActionRange) {
                    swipeLeftIndicate(e, { startY, startX, xUp, yUp, xDiff, yDiff, absXDiff, absYDiff });
                }
            } else {
                /* right swipe */
                // console.log('right swipe', { e, startY, startX, xDiff, swipeRight });
                // eslint-disable-next-line no-lonely-if
                // if (absXDiff > actionRange || absXDiff > indicateActionRange) {
                //     e.preventDefault();
                //     e.stopPropagation();
                // }
                if (absXDiff > actionRange) {
                    swipeRight(e, { startY, startX, xUp, yUp, xDiff, yDiff, absXDiff, absYDiff });
                }
                if (absXDiff > indicateActionRange) {
                    swipeRightIndicate(e, { startY, startX, xUp, yUp, xDiff, yDiff, absXDiff, absYDiff });
                }
            }
        } else if ( yDiff > 0 ) {
            /* up swipe */
            const { swipeUpPx = 0 } = this.state;
            const { scrollHeight, scrollTop, clientHeight } = this.appContainer;
            const isBottom = Math.round(scrollTop + clientHeight) >= scrollHeight - swipeUpPx;
            // console.log({ isBottom, swipeUpPx, scrollHeight, scrollTop, clientHeight });
            // console.log('up swipe', { startY, startX, yDiff });
            // if (absYDiff > actionRange || absYDiff > indicateActionRange) {
            //     e.preventDefault();
            //     e.stopPropagation();
            // }
            if (isBottom && absYDiff > indicateActionRange) {
                this.setState({
                    swipeUpPx: absYDiff < maxRange ? absYDiff : maxRange,
                });
            }
            if (absYDiff > actionRange) {
                swipeUp(e, { startY, startX, xUp, yUp, xDiff, yDiff, absXDiff, absYDiff });
            }
            if (absYDiff > indicateActionRange) {
                swipeUpIndicate(e, { startY, startX, xUp, yUp, xDiff, yDiff, absXDiff, absYDiff });
            }
        } else {
            // console.log({ absXDiff, absYDiff });
            /* down swipe */
            // console.log('down swipe', { startY, startX, yDiff });
            // if (absYDiff > actionRange || absYDiff > indicateActionRange) {
            //     e.preventDefault();
            //     e.stopPropagation();
            // }

            if (absYDiff > actionRange) {
                swipeDown(e, { startY, startX, xUp, yUp, xDiff, yDiff, absXDiff, absYDiff });
            }
            if (absYDiff > indicateActionRange) {
                swipeDownIndicate(e, { startY, startX, xUp, yUp, xDiff, yDiff, absXDiff, absYDiff });
            }
            const skipRefresh = e.target.closest('.skipRefresh');
            if (!skipRefresh) {
                const { scrollTop } = this.appContainer;
                // Elastic damping: pull feels heavier as you go further
                const elasticPull = maxRange * (1 - Math.exp(-absYDiff * 2.5 / maxRange));
                if (scrollTop <= 0 && absYDiff > refreshRange && !refreshing) {
                    this.setState({
                        refreshing: true,
                        readyToRefresh: false,
                        swipeDownPx: elasticPull,
                    });
                    // Haptic feedback
                    if (navigator.vibrate) {
                        navigator.vibrate(10);
                    }
                    this.pullToRefreshPage();
                } else if (scrollTop <= 0 && absYDiff > readyToRefreshRange && !refreshing) {
                    this.setState({
                        readyToRefresh: true,
                        swipeDownPx: elasticPull,
                    });
                    this.readyToRefreshTimer = setTimeout(() => this.setState({ readyToRefresh: false }), 1500);
                } else if (scrollTop <= 0 && absYDiff > indicateActionRange) {
                    this.setState({
                        swipeDownPx: elasticPull,
                    });
                }
            }
        }
    }

    scrollToTop = () => {
        if (this.appContainer) {
            localUtil.scrollTo(this.appContainer);
            this.refreshPage();
        }
    }

    doneRefreshing() {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => this.setState({
            startY: undefined,
            refreshing: false,
            readyToRefresh: false,
            swipeDownPx: 0,
        }), 1000);

    }

    setRefreshTime = () => {
        const refreshTime= new Date().getTime();
        this.setState({ refreshTime });
    }

    pullToRefreshPage = () => {
        const { currentUrl } = this.state;
        this.refreshPage();
        return this.doneRefreshing();
    }

    clickOnLogo = () => {
        const { currentUrl } = this.state;
        appState.noteLogoClick();
        appState.clickOnLogo(currentUrl);
    }

    toggleQuickSearch = () => {
        if (this.quickSearchRef) {
            this.quickSearchRef.toggleSearch();
        }
    }

    refreshPage = () => {
        const { currentUrl } = this.state;
        // console.log('refreshPage', currentUrl);
        // util.set('frontpage.view', 'homepage');
        try {
            userStore.onEvent('refreshPage', { currentUrl });
        } catch (err) {
            console.error('Error sending event:', err);
        }

        const { user } = userStore;
        try {
            appState.addUsageLog({
                type: 'refresh',
                url: currentUrl,
                timestamp: Math.floor(new Date().getTime() / 1000),
                browser: getBrowserInfo(),
                user: user ? user.id : null,
            });
            const urlRegexpFrontpage = new RegExp(`^/$`);
            const urlRegexpStart = new RegExp(`^/start`);
            const urlRegexpInstallations = new RegExp(`^/installations`);
            const urlRegexpUsersProfile = new RegExp(`^/users/profile`);
            const urlRegexpDyrejournal = new RegExp(`^/dyrejournal`);
            const urlRegexpMedia = new RegExp(`^/media`);
            const urlRegexpArticle = new RegExp(`^/article`);
            const urlRegexpRace = new RegExp(`^/race`);
            const urlRegexpTimeTracker = new RegExp(`^/timetracker`);
            const urlRegexpContact = new RegExp(`^/contact`);
            const urlRegexpCalendar = new RegExp(`^/calendar`);
            const urlRegexpCalendarEvent = new RegExp(`^/calendarevent`);
            const urlRegexpCalendarAvailability = new RegExp(`^/calendaravailability`);
            const urlRegexpTextTemplate = new RegExp(`^/texttemplate`);
            const urlRegexpSms = new RegExp(`^/sms`);
            const urlRegexpMailSent = new RegExp(`^/mailsent`);
            const urlRegexpErrors = new RegExp(`^/errors`);
            const urlRegexpApiLogger = new RegExp(`^/apilogger`);
            const urlRegexpQueryLogger = new RegExp(`^/querylogger`);
            const urlRegexpConvention = new RegExp(`^/convention`);
            const urlRegexpProduct = new RegExp(`^/product`);
            const urlRegexpContentCuration = new RegExp(`^/admin/content-curation`);
            const urlRegexpYearlySummary = new RegExp(`^/yearlysummary`);
            const urlRegexpFeed = new RegExp(`^/feed`);
            const urlRegexpMarketplace = new RegExp(`^/marketplace`);
            const urlRegexpCart = new RegExp(`^/cart`);
            const urlRegexpStatistics = new RegExp(`^/statistics`);
            const urlRegexpUsageStats = new RegExp(`^/usage-stats`);
            const urlRegexpSettings = new RegExp(`^/settings`);

            if (urlRegexpFrontpage.test(currentUrl)) {
                otherStores.installationStore?.refreshPage();
                otherStores.newsStore?.refreshPage();
                otherStores.newsStore?.runRefreshPageFunctions();
                otherStores.raceStore?.refreshPage();
                articleStore.refreshPage();
                articleStore.runRefreshPageFunctions();
                otherStores.conventionStore?.refreshPage();
            } else if (urlRegexpInstallations.test(currentUrl)) {
                otherStores.installationStore?.refreshPage();
            } else if (urlRegexpUsersProfile.test(currentUrl)) {
                userStore.refreshPage();
                articleStore.refreshPage();
                articleStore.runRefreshPageFunctions();
                otherStores.conventionStore?.refreshPage();
                otherStores.competitionResultStore?.refreshPage();
            } else if (urlRegexpDyrejournal.test(currentUrl)) {
                saleStore.refreshPage();
                salePaymentStore.refreshPage();
                saleReportStore.refreshPage();
            } else if (urlRegexpMedia.test(currentUrl)) {
                mediaStore.refreshPage();
            } else if (urlRegexpArticle.test(currentUrl)) {
                articleStore.refreshPage();
            } else if (urlRegexpRace.test(currentUrl)) {
                otherStores.raceStore?.refreshPage();
                otherStores.raceStore?.runRefreshPageFunctions();
                otherStores.newsStore?.refreshPage();
                otherStores.newsStore?.runRefreshPageFunctions();
            } else if (urlRegexpTimeTracker.test(currentUrl)) {
                otherStores.timeTrackerStore?.refreshPage();
            } else if (urlRegexpContact.test(currentUrl)) {
                contactStore.refreshPage();
            } else if (urlRegexpCalendar.test(currentUrl)) {
                calendarStore.refreshPage();
            } else if (urlRegexpCalendarEvent.test(currentUrl)) {
                calendarEventStore.refreshPage();
            } else if (urlRegexpCalendarAvailability.test(currentUrl)) {
                calendarAvailabilityStore.refreshPage();
            } else if (urlRegexpTextTemplate.test(currentUrl)) {
                textTemplateStore.refreshPage();
            } else if (urlRegexpSms.test(currentUrl)) {
                smsStore.refreshPage();
            } else if (urlRegexpMailSent.test(currentUrl)) {
                mailSentStore.refreshPage();
            } else if (urlRegexpConvention.test(currentUrl)) {
                otherStores.conventionStore?.refreshPage();
            } else if (urlRegexpProduct.test(currentUrl)) {
                productStore.refreshPage();
            } else if (urlRegexpYearlySummary.test(currentUrl)) {
                otherStores.yearlySummaryStore?.refreshPage();
            } else if (urlRegexpFeed.test(currentUrl)) {
                articleStore.refreshPage();
                articleStore.runRefreshPageFunctions();
            } else if (urlRegexpMarketplace.test(currentUrl)) {
                PubSub.publish(topics.REFRESH_PAGE, { page: 'marketplace' });
            } else if (urlRegexpCart.test(currentUrl)) {
                userStore.loadCart();
            } else if (urlRegexpStatistics.test(currentUrl)) {
                appState.loadStatistics({});
            } else if (urlRegexpUsageStats.test(currentUrl)) {
                PubSub.publish(topics.REFRESH_PAGE, { page: 'usage-stats' });
            } else if (urlRegexpErrors.test(currentUrl)) {
                appState.loadErrors({ limit: 100, offset: 0 });
            } else if (urlRegexpApiLogger.test(currentUrl)) {
                appState.loadApiLogs({ limit: 100, offset: 0, sanitized: true });
            } else if (urlRegexpQueryLogger.test(currentUrl)) {
                appState.loadQueryLogs({ limit: 100, offset: 0 });
            } else if (urlRegexpContentCuration.test(currentUrl)) {
                otherStores.contentSourceStore?.refreshPage();
                otherStores.scrapedContentStore?.refreshPage();
                otherStores.curatedContentStore?.refreshPage();
            } else if (urlRegexpSettings.test(currentUrl)) {
                userStore.refreshPage();
            } else if (urlRegexpStart.test(currentUrl)) {
                this.setRefreshTime(currentUrl);
                return this.doneRefreshing();
            }
        } catch (err) {
            console.log(err);
        }
    }

    onScroll = (e) => {
        const { currentUrl } = this.state;
        const element = e.target;
        const { scrolledDown } = this.state;
        if (element) {
            if (element.scrollTop > SCROLLDOWN_LIMIT && !scrolledDown) {
            // if (element.scrollTop > SCROLLDOWN_LIMIT) {
                this.setState({
                    scrolledDown: element.scrollTop,
                });
            } else if (element.scrollTop === 0) {
                this.setState({
                    scrolledDown: 0,
                });
            }
        }

        // Check if we should hide the navbar
        const { isMobile } = appState;
        if (isMobile) {
            const currentScrollY = element.scrollTop;
            const scrollDelta = currentScrollY - this.lastScrollY;

            if (currentScrollY === 0) {
                appState.updateKeyValue('isVisibleNavbar', true);
            } else if (scrollDelta > MENU_HIDE_SCROLLDOWN) {
                appState.updateKeyValue('isVisibleNavbar', false);
            } else if (scrollDelta < -MENU_HIDE_SCROLLDOWN) {
                appState.updateKeyValue('isVisibleNavbar', true);
            }
            this.lastScrollY = currentScrollY;
        }

        // Load more content function
        const { isAtBottom } = this.state;
        const marginToBottom = element.clientHeight;
        if (element.scrollTop + element.clientHeight + marginToBottom >= element.scrollHeight) {
            if (!isAtBottom) {
                this.setState({ isAtBottom: true });
                // console.log({ currentUrl });
                const regex1 = /^\/admin\/customer$/;
                const regex2 = /^\/admin\/user$/;
                const regex3 = /^\/admin\/installation$/;
                const regex4 = /^\/admin\/part$/;
                const regex5 = /^\/admin\/inspection$/;
                const regex6 = /^\/admin\/loan$/;
                const regex7 = /^\/admin\/news$/;

                const regex8 = /^\/admin\/journal$/;
                const regex9 = /^\/admin\/prescripton$/;
                const regex10 = /^\/admin\/drug$/;
                const regex11 = /^\/admin\/animal$/;
                const regex12 = /^\/admin\/visitor$/;
                const regex13 = /^\/admin\/sale$/;
                const regex14 = /^\/admin\/product$/;
                const regex15 = /^\/admin\/diagnose$/;
                const regex16 = /^\/admin\/file$/;
                const regex17 = /^\/admin\/usage$/;

                const regex18 = /^\/admin\/media$/;
                const regex51 = /^\/admin\/article$/;
                const regex19 = /^\/admin\/race$/;
                const regex20 = /^\/admin\/raceclass$/i;
                const regex21 = /^\/admin\/raceclasscontestant$/i;
                const regex27 = /^\/admin\/raceresult$/i;

                const regex22 = /^\/admin\/timetracker$/i;

                const regex23 = /^\/admin\/calendar$/;
                const regex24 = /^\/admin\/calendarevent$/i;
                const regex28 = /^\/admin\/calendaravailability$/i;
                const regex25 = /^\/admin\/texttemplate$/i;
                const regex26 = /^\/admin\/sms$/;
                const regex29 = /^\/admin\/gpspoint$/i;
                const regex30 = /^\/admin\/salepayment$/i;
                const regex40 = /^\/admin\/animalspecie$/i;
                const regex31 = /^\/admin\/animalbreed$/i;
                const regex32 = /^\/admin\/salereport$/i;
                const regex33 = /^\/admin\/gpsdevice$/i;
                const regex34 = /^\/admin\/stream$/;
                const regex35 = /^\/admin\/streamdevice$/i;
                const regex36 = /^\/admin\/mailsent$/i;
                const regex39 = /^\/admin\/contact$/;

                const regex41 = /^\/admin\/convention$/;
                const regex42 = /^\/admin\/conventionresult$/;

                const regex43 = /^\/admin\/competitionresult$/;

                const regex50 = /^\/race.+?\/news$/;

                const regex60 = /^\/article-list$/;
                const regex65 = /^\/product$/;
                const regex66 = /^\/products\//;

                const regex90 = /^\/$/;

                switch (true) {
                    case regex50.test(currentUrl):
                        return otherStores.newsStore?.loadMore();

                    case regex90.test(currentUrl):
                        otherStores.ffeStore?.loadMore();
                        articleStore.runLoadMoreFunctions();
                        return articleStore.loadMore();
                    case regex60.test(currentUrl):
                        articleStore.runLoadMoreFunctions();
                        return articleStore.loadMore();
                    case /^\/feed/.test(currentUrl):
                        articleStore.runLoadMoreFunctions();
                        return articleStore.loadMore();
                    case /^\/marketplace/.test(currentUrl):
                        articleStore.runLoadMoreFunctions();
                        return articleStore.loadMore();
                    case regex65.test(currentUrl):
                        return productStore.loadMore();
                    case regex66.test(currentUrl):
                        return productStore.loadMore();
                    case regex1.test(currentUrl):
                        return customerStore.loadMore();
                    case regex2.test(currentUrl):
                        return userStore.loadMore();
                    case regex3.test(currentUrl):
                        return otherStores.installationStore?.loadMore();
                    case regex4.test(currentUrl):
                        return otherStores.partStore?.loadMore();
                    case regex5.test(currentUrl):
                        return otherStores.inspectionStore?.loadMore();
                    case regex6.test(currentUrl):
                        return otherStores.loanStore?.loadMore();
                    case regex7.test(currentUrl):
                        return otherStores.newsStore?.loadMore();

                    case regex8.test(currentUrl):
                        return journalStore.loadMore();
                    case regex9.test(currentUrl):
                        return prescriptionStore.loadMore();
                    case regex10.test(currentUrl):
                        return drugStore.loadMore();
                    case regex11.test(currentUrl):
                        return animalStore.loadMore();
                    case regex40.test(currentUrl):
                        return animalSpecieStore.loadMore();
                    case regex31.test(currentUrl):
                        return animalBreedStore.loadMore();
                    case regex12.test(currentUrl):
                        return visitorStore.loadMore();
                    case regex13.test(currentUrl):
                        return saleStore.loadMore();
                    case regex14.test(currentUrl):
                        return productStore.loadMore();
                    case regex15.test(currentUrl):
                        return diagnoseStore.loadMore();
                    case regex16.test(currentUrl):
                        return fileStore.loadMore();
                    case regex17.test(currentUrl):
                        return usageStore.loadMore();

                    case regex18.test(currentUrl):
                        return mediaStore.loadMore();
                    case regex51.test(currentUrl):
                        return articleStore.loadMore();
                    case regex19.test(currentUrl):
                        return otherStores.raceStore?.loadMore();
                    case regex20.test(currentUrl):
                        return otherStores.raceClassStore?.loadMore();
                    case regex21.test(currentUrl):
                        return otherStores.raceClassContestantStore?.loadMore();
                    case regex27.test(currentUrl):
                        return otherStores.raceResultStore?.loadMore();
                    case regex22.test(currentUrl):
                        return otherStores.timeTrackerStore?.loadMore();
                    case regex23.test(currentUrl):
                        return calendarStore.loadMore();
                    case regex24.test(currentUrl):
                        return calendarEventStore.loadMore();
                    case regex28.test(currentUrl):
                        return calendarAvailabilityStore.loadMore();
                    case regex25.test(currentUrl):
                        return textTemplateStore.loadMore();
                    case regex26.test(currentUrl):
                        return smsStore.loadMore();
                    case regex36.test(currentUrl):
                        return mailSentStore.loadMore();
                    case regex29.test(currentUrl):
                        return otherStores.gpsPointStore?.loadMore();
                    case regex30.test(currentUrl):
                        return salePaymentStore.loadMore();
                    case regex32.test(currentUrl):
                        return saleReportStore.loadMore();
                    case regex33.test(currentUrl):
                        return otherStores.gpsDeviceStore?.loadMore();
                    case regex34.test(currentUrl):
                        return otherStores.streamStore?.loadMore();
                    case regex35.test(currentUrl):
                        return otherStores.streamDeviceStore?.loadMore();
                    case regex39.test(currentUrl):
                        return contactStore.loadMore();
                    case regex41.test(currentUrl):
                        return otherStores.conventionStore?.loadMore();
                    case regex42.test(currentUrl):
                        return otherStores.conventionResultStore?.loadMore();
                    case regex43.test(currentUrl):
                        return otherStores.competitionResultStore?.loadMore();

                    case regex10.test(currentUrl):
                        return otherStores.installationStore?.loadMore();
                }
            }
        } else if (isAtBottom) {
            this.setState({ isAtBottom: false });
        }
        // console.log({ scrollTop: element.scrollTop, clientHeight: element.clientHeight, scrollHeight: element.scrollHeight });
    }

    checkShareApi = () => {
        appState.checkShareApi();
    }

    handleReduceMotionChange = () => {
        const { user } = userStore;
        applyReduceMotion(user, { matchMedia: window.matchMedia?.bind(window), toggleBodyClasses: util.toggleBodyClasses });
    }

    // componentWillMount() {

    // }

    componentWillUnmount() {
        if (this.stopDialogLifecycle) this.stopDialogLifecycle();
        if (this.stopContrastPreference) this.stopContrastPreference();
        clearInterval(this.sendLogsTimer);
        clearInterval(this.performanceTimer);
        clearInterval(this.inboxTimer);
        clearInterval(this.notificationsTimer);
        if (this.inboxSignalUnsub) { this.inboxSignalUnsub(); this.inboxSignalUnsub = null; }
        clearInterval(this.supportTimer);
        clearInterval(this.supportOpenTimer);
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);
        if (this.reduceMotionMedia?.removeEventListener) {
            this.reduceMotionMedia.removeEventListener('change', this.handleReduceMotionChange);
        }
        if (this.stopNativeStatusBar) {
            this.stopNativeStatusBar();
            this.stopNativeStatusBar = null;
        }
    }

    setInputProps = () => {
        const inputProps = util.collectPropsFromElement(window.document.body);
        this.setState({ inputProps });
    }

    enforcePinSetupGate = () => {
        if (typeof window === 'undefined') return;
        const lockScreenEnabled = !!userStore?.customer?.featureFlags?.lockScreen?.enabled;
        const userLoggedIn = !!userStore?.user?.id;
        const userHasPin = !!userStore?.user?.hasPin;
        const hash = window.location.hash || '';
        const onSetPinPage = hash.indexOf('/profile/set-pin') !== -1;
        const onLogoutPage = hash.indexOf('/logout') !== -1;
        if (lockScreenEnabled && userLoggedIn && !userHasPin && !onSetPinPage && !onLogoutPage) {
            route('/profile/set-pin');
        }
    }

    componentDidMount() {
        this.stopDialogLifecycle = installDialogLifecycle({
            store: dialogStore,
            history,
            getContext: () => ({
                userId: userStore.user?.id ?? null,
                customerId: userStore.customer?.id ?? null,
                locked: lockScreenStore.isLocked,
            }),
        });
        // window.addEventListener("cordovacallbackerror", (event) => {
        //     // event.error contains the original error object
        //     console.log(event.error);
        // });

        const browserInfo = getBrowserInfo();
        const isMobile = browserInfo.os === 'Android' || browserInfo.os === 'iOS';
        appState.updateKeyValue('isMobile', isMobile);

        const inputProps = util.collectPropsFromElement(window.document.body);
        appState.setInputProps(inputProps);
        this.stopContrastPreference = watchContrastPreference({
            appState,
            userStore,
            document: window.document,
            matchMedia: window.matchMedia?.bind(window),
        });
        const apiServer = inputProps.apiServer || this.props.apiServer || `${window.location.protocol}//${window.location.host}`;

        // Parse the query string
        const queryParams = new URLSearchParams(window.location.search);
        const jwtToken = queryParams.get('jwtToken');
        const refreshTokenParam = queryParams.get('refreshToken');
        // Check if apiToken exists in the query string
        if (jwtToken) {
            console.log("Found apiToken:", jwtToken);
            // You can now use this apiToken as needed
            util.setJwtToken(jwtToken);
            if (refreshTokenParam) {
                localStorage.setItem('refreshToken', refreshTokenParam);
            }
            const { wantedPage } = appState;
            // console.log({ wantedPage });
            window.location.assign(`${window.location.protocol}//${window.location.host}/#${wantedPage}`);
        }

        util.setApiServer(apiServer);
        if (isNativeAppPage()) {
            // cordova.js is injected asynchronously by the page template; paint the
            // status bar in the header colour as soon as the native runtime is ready.
            this.stopNativeStatusBar = watchNativeStatusBar();
            // cordova-ios calls window.handleOpenURL(url) when the app is opened with its
            // custom URL scheme (Vipps return); expose it as a window event.
            installNativeDeepLinkBridge();
            // The shell cold-starts us at /index with no hash after iOS killed the
            // suspended app. Route back to where the user was; a present hash (deep
            // link, or cordova-ios reloading the page in place) is left alone.
            const { currentUrl: startRoute } = this.state;
            if (!startRoute || startRoute === '/') {
                const rememberedRoute = getRememberedNativeRoute();
                if (rememberedRoute) {
                    route(rememberedRoute, true);
                }
            }
        }
        // this.ensureCordova();
        this.checkShareApi();
        this.loadAll();
        this.overrideConsole();
        this.startLogTimer();

        // document.addEventListener('pause', this.onPause, false);
        // document.addEventListener('resume', this.onResume, false);
        // document.addEventListener('menubutton', this.onMenubutton, false);
        // document.addEventListener('backbutton', this.onBackbutton, false);
        // document.addEventListener('searchbutton', this.onSearchbutton, false);

        // document.addEventListener('visibilitychange', () => {
            // if (!document.hidden) {
            //     // The tab/browser is now active again
            //     // e.g., refresh data or resume timers
            //     // console.log('The tab/browser is now active again');
            // } else {
            //     // The page is backgrounded
            // }
        // });
        // window.addEventListener('focus', () => {
            // Browser window is focused again
            // console.log('Browser window is focused again');
            // this.refreshPage();
        // });
        // window.addEventListener('blur', () => {
        // // Browser window lost focus
        // });


        // Network status tracking for offline/online awareness
        appState.setConnectionStatus(navigator.onLine ? 'online' : 'offline');
        this.handleOnline = () => {
            appState.setConnectionStatus('online');
            // Retry application-level pending saves (idempotent field-level PATCHes)
            offlineRetryQueue.retryPendingSaves(appState);
            // Drain offline-saved entities (Phase 2 / 2c + article posts).
            // Per-type store callbacks live in lib/offlineDrain.js so manual
            // retries (pending sales route, feed pending cards) share them.
            drainQueues().catch(() => {});
        };
        this.handleOffline = () => appState.setConnectionStatus('offline');
        window.addEventListener('online', this.handleOnline);
        // Hydrate offline-queued sales into the list (Phase 2). Best-effort.
        try {
            saleStore.loadPendingSales();
        } catch (e) {
            // ignore — boot must not fail because of a queue read.
        }
        window.addEventListener('offline', this.handleOffline);
        // The 'online' event never fires for a session that STARTS online,
        // so a queue left behind by a previous offline session (app closed
        // while offline, or connectivity that failed requests without ever
        // flipping navigator.onLine) would sit in IndexedDB forever while
        // sales/journals already on the server reference its tempIds. Replay
        // it at boot too.
        if (navigator.onLine) {
            this.handleOnline();
        }

        // Re-authenticate Firebase and restart sync when returning from sleep.
        // Firebase custom tokens expire after 1 hour, so overnight sleep breaks
        // Firestore real-time sync. Debounced at 1 second to avoid rapid-fire calls.
        let firebaseReauthTimer = null;
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;

            const hasRefreshToken = !!localStorage.getItem('refreshToken');
            if (!hasRefreshToken) return; // Not logged in

            if (firebaseReauthTimer) {
                clearTimeout(firebaseReauthTimer);
            }
            firebaseReauthTimer = setTimeout(async () => {
                firebaseReauthTimer = null;
                try {
                    // Wait for the interceptor's token refresh to complete first
                    const pendingRefresh = getVisibilityRefreshPromise();
                    if (pendingRefresh) {
                        await pendingRefresh;
                    }

                    // Get fresh Firebase custom token from the server
                    await userStore.getInfo({ forceLoad: true });

                    // Re-start Firebase sync to re-establish Firestore listener
                    const { user, customer } = userStore;
                    if (user && user.id && customer && customer.id) {
                        const syncUserName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
                        firebaseSync.start({ customerId: customer.id, userId: user.id, userName: syncUserName });
                    }
                } catch (err) {
                    console.error('[Firebase re-auth on wake] Error:', err);
                }
            }, 1000);
        });

        appState.setAppContainer(this.appContainer);
        appState.storeDefaultMetaTags({});
        const { currentUrl } = this.state;
        appState.setPath(currentUrl);
        this.setInputProps();
        this.redirectIfNotLoggedIn({ url: currentUrl });
        const hostname = window.location.hostname;

        appState.setVersion(versionData, hostname);
        articleStore.setVersion(versionData, hostname);
        userStore.setVersion(versionData, hostname);
        otherStores.newsStore?.setVersion(versionData, hostname);
        mediaStore.setVersion(versionData, hostname);
        customerStore.setVersion(versionData, hostname);
        otherStores.installationStore?.setVersion(versionData, hostname);
        otherStores.partStore?.setVersion(versionData, hostname);
        otherStores.inspectionStore?.setVersion(versionData, hostname);
        otherStores.loanStore?.setVersion(versionData, hostname);
        usageStore.setVersion(versionData, hostname);
        otherStores.raceStore?.setVersion(versionData, hostname);
        otherStores.raceClassStore?.setVersion(versionData, hostname);
        otherStores.raceClassContestantStore?.setVersion(versionData, hostname);
        otherStores.raceResultStore?.setVersion(versionData, hostname);
        journalStore.setVersion(versionData, hostname);
        prescriptionStore.setVersion(versionData, hostname);
        drugStore.setVersion(versionData, hostname);
        animalStore.setVersion(versionData, hostname);
        animalSpecieStore.setVersion(versionData, hostname);
        animalBreedStore.setVersion(versionData, hostname);
        visitorStore.setVersion(versionData, hostname);
        saleStore.setVersion(versionData, hostname);
        salePaymentStore.setVersion(versionData, hostname);
        saleReportStore.setVersion(versionData, hostname);
        productStore.setVersion(versionData, hostname);
        diagnoseStore.setVersion(versionData, hostname);
        fileStore.setVersion(versionData, hostname);
        otherStores.gpsPointStore?.setVersion(versionData, hostname);
        otherStores.gpsDeviceStore?.setVersion(versionData, hostname);
        otherStores.streamStore?.setVersion(versionData, hostname);
        otherStores.streamDeviceStore?.setVersion(versionData, hostname);
        otherStores.conventionStore?.setVersion(versionData, hostname);
        // Course loads send `hostname` so get-course.js scopes to the domain
        // customer instead of the viewer's memberships (TICKET-00010).
        otherStores.courseStore?.setVersion(versionData, hostname);
        otherStores.conventionResultStore?.setVersion(versionData, hostname);
        otherStores.competitionResultStore?.setVersion(versionData, hostname);
        otherStores.timeTrackerStore?.setVersion(versionData, hostname);
        contactStore.setVersion(versionData, hostname);
        dealerStore.setVersion(versionData, hostname);
        pricelistStore.setVersion(versionData, hostname);
        calendarStore.setVersion(versionData, hostname);
        calendarEventStore.setVersion(versionData, hostname);
        calendarAvailabilityStore.setVersion(versionData, hostname);
        textTemplateStore.setVersion(versionData, hostname);
        smsStore.setVersion(versionData, hostname);
        mailSentStore.setVersion(versionData, hostname);
        otherStores.ffeStore?.setVersion(versionData, hostname);
        // console.log('versionData', versionData);
        // window.addEventListener('scroll', ()=>{
        //     console.log(document.documentElement.scrollTop || document.body.scrollTop);
        // })

        this.reduceMotionMedia = window.matchMedia ? window.matchMedia(REDUCE_MOTION_QUERY) : null;
        if (this.reduceMotionMedia?.addEventListener) {
            this.reduceMotionMedia.addEventListener('change', this.handleReduceMotionChange);
        }
    }

    render() {
        const {
            refreshing,
            // jwtToken,
            readyToRefresh,
            swipeDownPx,
            swipeUpPx,
            currentUrl,
            // refreshTime,
            // scrolledDown,
            // bug = {},
            inputProps,
            isTouchDragging,
            online,
            sessionWarning,
        } = this.state;
        const scrolledDownPlaceholder = false;
        const { path } = this.props;
        const {
            isCordova,
            showDrawer,
            showTopSheet,
            topSheetHeight,
            language: appStateLanguage,
            currentCustomer = {},
            path: appStatePath,
        } = appState;
        const { isAdmin, isRaceAdmin, isVeterinary, user = {}, register = {} } = userStore;
        const { language = appStateLanguage } = user;

        const languageDef = countryMap[language || 'default'];
        // appDiv.style.paddingTop = `calc(${currentCustomer.logoHeight} - 45px)`;
        // const paddingTop = `padding-top: calc(${currentCustomer?.logoHeight || '0px'} - 45px);`;
        // console.log('currentCustomer?.logoHeight', currentCustomer?.logoHeight)
        const baseLogoPadding = currentCustomer?.logoHeight || '45px';
        const topSheetOffset = showTopSheet ? (topSheetHeight || '25vh') : '0px';
        let paddingTop = `padding-top: calc(${baseLogoPadding} + ${topSheetOffset});`;
        // console.log('appStatePath', appStatePath);
        if (/^\/(welcome|login|register|forgotten|waiting-approval|window|calendarBooking|prescriptionOrder|onBoarding|race\/\d+\/\d+\/tracking|race\/\d+\/tracking|buy\/)/.test(appStatePath)) {
            paddingTop = `padding-top: ${topSheetOffset};`;
        }
        // console.log('paddingTop', paddingTop)

        // ${readyToRefresh ? 'padding-top: 50px;' : ''}
        // ${refreshing ? 'padding-top: 70px;' : ''}
        // overscroll-behavior: none;
        // ${swipeDownPx ? `padding-top: ${swipeDownPx}px !important;` : paddingTop}

        const isPublicRoute = currentUrl.match(/^\/public/);
        if (currentUrl.match(/^\/tv\//) || isPublicRoute) {
            const bgStyle = isPublicRoute
                ? 'background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);'
                : 'background-color: transparent; background: transparent;';
            return (
                <IntlProvider definition={languageDef}>
                    <Catcher>
                        <DialogHost />
                        <style>
                            {`
                                html, body.bootstrap {
                                    ${isPublicRoute ? 'background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);' : 'background-color: transparent; background: transparent;'}
                                }
                            `}
                        </style>
                        <div id='app'
                            style={`
                                overflow-x: auto;
                                overflow-y: ${showDrawer ? 'hidden': 'auto'};
                                height: 100%;
                                min-height: 100vh;
                                ${readyToRefresh ? 'padding-top: 50px;' : ''}
                                ${refreshing ? 'padding-top: 70px;' : ''}
                                transition: .3s; overscroll-behavior: contain;
                                ${bgStyle}
                            `}
                            ref={c => this.appContainer = c}
                            onScroll={this.onScroll}
                        >
                            <Router history={history}>
                                <AsyncRoute stores={this.stores} {...this.props} path="/public/calendar/:customerId" getComponent={() => import('./routes/public/calendar').then(module => module.default)} />

                                {otherPublicRoutes.map(route => (
                                    <AsyncRoute stores={this.stores} {...this.props} {...(route.props || {})} key={`other-public-${route.path}`} path={route.path} getComponent={route.getComponent} />
                                ))}
                                {signingPublicRoutes.map(route => (
                                    <AsyncRoute stores={this.stores} {...this.props} {...(route.props || {})} key={`signing-public-${route.path}`} path={route.path} getComponent={route.getComponent} />
                                ))}

                                <LoadingSpinner default goto='/' gotoTitle='Home' routerName='public' />
                            </Router>
                        </div>
                    </Catcher>
                </IntlProvider>
            );
        }


        return (
            <IntlProvider definition={languageDef}>
                <Catcher>
                    <DialogHost />
                    <div
                        id='app'
                        class='bg-light'
                        style={`
                            overflow-x: auto;
                            overflow-y: ${showDrawer ? 'hidden': 'auto'};
                            height: var(--app-viewport-height, 100vh);
                            ${paddingTop}
                            ${swipeUpPx ? `padding-bottom: ${swipeUpPx}px !important;` : ''}
                            transition: ${isTouchDragging ? '' : 'padding 0.2s ease-in-out'};
                        `}
                        onTouchstart={this.touchStart}
                        onTouchend={this.touchEnd}
                        onTouchmove={this.touchMove}
                        onScroll={this.onScroll}
                        ref={c => this.appContainer = c}
                    >
                        <LockScreen stores={this.stores} />
                        <Progress stores={this.stores} scrolledDown={scrolledDownPlaceholder} />
                        <Match>
                            {({ url }) => /^\/(race\/\d+\/\d+\/tracking|race\/\d+\/tracking)/.test(url) ? null : (
                                <>
                                    <ChristmasLights stores={this.stores} position='top' online={online} />
                                    <HalloweenDecoration stores={this.stores} />
                                </>
                            )}
                        </Match>
                        {/* {readyToRefresh ? <ReadyToRefresh stores={this.stores} height={swipeDownPx} scrolledDown={scrolledDownPlaceholder} /> : <></>} */}
                        {/* {refreshing ? <RefreshSpinner stores={this.stores} height={swipeDownPx || refreshRange} scrolledDown={scrolledDownPlaceholder} /> : <></>} */}
                        <RefreshSpinner stores={this.stores} height={refreshing ? (swipeDownPx || refreshRange) : swipeDownPx || 0} refreshing={refreshing} scrolledDown={scrolledDownPlaceholder} />

                        {sessionWarning && (
                            <SessionWarningBanner
                                onRetry={async () => {
                                    const { installFetchApiInterceptor } = await import('./lib/fetchApiInterceptor');
                                    // Trigger a proactive refresh attempt which will dismiss the warning on success
                                    const refreshToken = localStorage.getItem('refreshToken');
                                    if (refreshToken) {
                                        const apiServer = util.getApiServer();
                                        try {
                                            const rawResponse = await fetch(`${apiServer}/api/sessions/refresh`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ refreshToken }),
                                            });
                                            const refreshResponse = await rawResponse.json();
                                            if (rawResponse.status === 200 && refreshResponse.data) {
                                                util.setJwtToken(refreshResponse.data.accessToken);
                                                localStorage.setItem('refreshToken', refreshResponse.data.refreshToken);
                                                this.setState({ sessionWarning: false });
                                            }
                                        } catch (e) {
                                            // Still offline — banner stays
                                        }
                                    }
                                }}
                                onDismiss={() => this.setState({ sessionWarning: false })}
                            />
                        )}

                        {/* {!online ? <>
                            <Offline stores={this.stores} {...this.props} />
                        </> : <></>} */}

                        <Match>
                            {({ matches, path, url }) => <>
                                {/^\/(welcome|login|register|forgotten|waiting-approval|window|calendarBooking|prescriptionOrder|onBoarding|race\/\d+\/\d+\/tracking|race\/\d+\/tracking)/.test(url) ? <>
                                    {/* <pre><br /><br /><br />url: {url}<br />path: {path}</pre> */}
                                </> : <>
                                    <Header stores={this.stores} {...this.props} scrolledDown={scrolledDownPlaceholder} refreshPage={this.refreshPage} clickOnLogo={this.clickOnLogo} toggleQuickSearch={this.toggleQuickSearch} />
                                </>}
                            </>}
                        </Match>

                        <div class='d-flex'>
                            <Match>
                                {({ matches, path, url }) => <>
                                    {/^\/(welcome|login|register|forgotten|waiting-approval|window|calendarBooking|prescriptionOrder|onBoarding|race\/\d+\/\d+\/tracking|race\/\d+\/tracking)/.test(url) ? null : <>
                                        <Sidebar stores={this.stores} toggleQuickSearch={this.toggleQuickSearch} />
                                    </>}
                                </>}
                            </Match>
                            <div class='flex-grow-1' style='min-width: 0;'>
                        <Router onChange={this.handleRoute} history={history}>
                            <AsyncRoute stores={this.stores} {...this.props} path="/waiting-approval" getComponent={() => import('./routes/waiting-approval').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} path="/welcome" getComponent={() => import('./routes/welcome').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} path="/logo" getComponent={() => import('./routes/logo').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} path="/login" getComponent={() => import('./routes/login/phone').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} path="/login/email" getComponent={() => import('./routes/login').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} path="/login/email/:email" getComponent={() => import('./routes/login').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} path="/link/login/:loginToken" getComponent={() => import('./routes/login').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} path="/register/" getComponent={() => import('./routes/register').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} path="/register/:email" getComponent={() => import('./routes/register').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} path="/forgotten/" getComponent={() => import('./routes/forgotten').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} path="/forgotten/:email" getComponent={() => import('./routes/forgotten').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} path="/logout/" getComponent={() => import('./routes/logout').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} path="/info/privacy" getComponent={() => import('./routes/info/privacy').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/onBoarding' getComponent={() => import('./routes/onBoarding').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/onBoardingClub' getComponent={() => import('./routes/onBoardingClub').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/users/profile' getComponent={() => import('./routes/users/profile').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/users/profile/:view' getComponent={() => import('./routes/users/profile').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/users/profile/:view/:uuidv4' getComponent={() => import('./routes/users/profile').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/profile/set-pin' getComponent={() => import('./routes/profile/setPin').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} path='/settings/notifications' getComponent={() => import('./routes/settings/notifications').then(module => module.default)} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/help' getComponent={() => import('./routes/help').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/help/search/:initialSearch' getComponent={() => import('./routes/help').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/help/:id' getComponent={() => import('./routes/help').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/support' getComponent={() => import('./routes/support').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/support/new' getComponent={() => import('./routes/support/new').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/support/refund' getComponent={() => import('./routes/support/refund').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/support/staff' getComponent={() => import('./routes/support/staff').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/support/ticket/:uuidv4' getComponent={() => import('./routes/support/ticket').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/calendar' getComponent={() => import('./routes/calendar').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/calendar/notifications' calendar='notification' getComponent={() => import('./routes/calendar').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/calendarBooking' getComponent={() => import('./routes/calendarBooking').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/calendarBooking/:customerId' getComponent={() => import('./routes/calendarBooking').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/prescriptionOrder/:customerId' getComponent={() => import('./routes/dyrejournal/prescriptionOrderPublic').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-sale' getComponent={() => import('./routes/dyrejournal/sale').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-sale/:sale' getComponent={() => import('./routes/dyrejournal/sale').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-mobile' getComponent={() => import('./routes/dyrejournal/saleMobile').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-mobile/:sale' getComponent={() => import('./routes/dyrejournal/saleMobile').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-payments' getComponent={() => import('./routes/dyrejournal/paymentList').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-sms' getComponent={() => import('./routes/dyrejournal/smsList').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-sms-reminder-log' getComponent={() => import('./routes/dyrejournal/smsReminderLog').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-no-shows' getComponent={() => import('./routes/dyrejournal/noShowList').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-mailsent' getComponent={() => import('./routes/dyrejournal/mailSentList').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-pending-sales' getComponent={() => import('./routes/dyrejournal/pendingSales').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-files' getComponent={() => import('./routes/dyrejournal/filesList').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-contacts' getComponent={() => import('./routes/dyrejournal/contactList').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-phone-consultations' getComponent={() => import('./routes/dyrejournal/phoneConsultations').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-prescription-orders' getComponent={() => import('./routes/dyrejournal/prescriptionOrders').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-clinic' getComponent={() => import('./routes/dyrejournal/clinic').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-lab-results' getComponent={() => import('./routes/dyrejournal/idexxLabResults').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-idexx-mapping' getComponent={() => import('./routes/dyrejournal/idexxMapping').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-kpi' getComponent={() => import('./routes/dyrejournal/kpiDashboard').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-accounts' getComponent={() => import('./routes/dyrejournal/accountsOverview').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/visitor' getComponent={() => import('./routes/visitor').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/animal' getComponent={() => import('./routes/animal').then(module => module.default)} loading={() => <div>loading...</div>} />
                            {/* OAuth consent + AI connections (Rammeverket, B) — spec 2026-10-06-partner-visits-api-design.md */}
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/mcp-authorize' getComponent={() => import('./routes/mcpAuthorize').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/mcp-connections' getComponent={() => import('./routes/mcpConnections').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin/idexx' getComponent={() => import('./routes/admin/idexx').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps}
                                scrolledDown={scrolledDownPlaceholder}
                                path='/admin/api-tokens'
                                getComponent={() => import('./routes/admin/apiTokens').then(module => module.default)}
                                loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps}
                                scrolledDown={scrolledDownPlaceholder}
                                path='/admin/api-tokens/:id'
                                getComponent={() => import('./routes/admin/apiTokens').then(module => module.default)}
                                loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps}
                                scrolledDown={scrolledDownPlaceholder}
                                path='/admin/oauth-clients'
                                getComponent={() => import('./routes/admin/oauthClients').then(module => module.default)}
                                loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps}
                                scrolledDown={scrolledDownPlaceholder}
                                path='/admin/feature-usage'
                                getComponent={() => import('./routes/admin/featureUsage').then(module => module.default)}
                                loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps}
                                scrolledDown={scrolledDownPlaceholder}
                                path='/admin/feature-usage/:feature'
                                getComponent={() => import('./routes/admin/featureUsage').then(module => module.default)}
                                loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps}
                                scrolledDown={scrolledDownPlaceholder}
                                path='/admin/feature-usage/:feature/:customerId'
                                getComponent={() => import('./routes/admin/featureUsage').then(module => module.default)}
                                loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin/product-lists' getComponent={() => import('./routes/admin/productLists').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/pricelists' getComponent={() => import('./routes/pricelists').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/pricelists/:id' getComponent={() => import('./routes/pricelists/detail').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin/ai-feedback' getComponent={() => import('./routes/admin/aiFeedback').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin/images' getComponent={() => import('./routes/admin/images').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin/development' getComponent={() => import('./routes/admin/development').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin/development-ai' getComponent={() => import('./routes/admin/development-ai').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin' getComponent={() => import('./routes/admin').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin/:store' getComponent={() => import('./routes/admin').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/errors' getComponent={() => import('./routes/admin/errors').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/statistics' getComponent={() => import('./routes/admin/statistics').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/usage-stats' getComponent={() => import('./routes/admin/usage-stats').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/apilogger' getComponent={() => import('./routes/admin/apilogger').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/querylogger' getComponent={() => import('./routes/admin/querylogger').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/dyrejournal-admin' getComponent={() => import('./routes/admin/dyrejournal-admin').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/pistolklubb-admin' getComponent={() => import('./routes/admin/pistolklubb-admin').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/admin-sales' getComponent={() => import('./routes/admin/sales').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/ticketScanner' getComponent={() => import('./routes/ticketScanner').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/texttemplates' getComponent={() => import('./routes/texttemplates').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/buy/:id' getComponent={() => import('./routes/products/buy').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/product/:uuidv4' getComponent={() => import('./routes/products/detail').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/product/:title/:uuidv4' getComponent={() => import('./routes/products/detail').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/products/imports' getComponent={() => import('./routes/products/imports').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/products/:uuidv4' getComponent={() => import('./routes/products/detail').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/products/pick/:id' getComponent={() => import('./routes/products/pick').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/products/qrcodeScanner' getComponent={() => import('./routes/products/qrcodeScanner').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/products/barcodeScanner' getComponent={() => import('./routes/products/barcodeScanner').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/products' getComponent={() => import('./routes/products').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/products/:customer' getComponent={() => import('./routes/products/listFrontpage').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/products/table' getComponent={() => import('./routes/products/table').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/cart/add/:uuidv4' getComponent={() => import('./routes/cart/add').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/cart' getComponent={() => import('./routes/cart').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/checkout/:saleId' getComponent={() => import('./routes/checkout').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/media' getComponent={() => import('./routes/media').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/sms' getComponent={() => import('./routes/sms').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/notifications' getComponent={() => import('./routes/notifications').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/notification-logs' getComponent={() => import('./routes/notification-logs').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/mailsent' getComponent={() => import('./routes/mailsent').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/window/close' getComponent={() => import('./routes/window/close').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/window/payment-done' getComponent={() => import('./routes/window/paymentDone').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/window/404' getComponent={() => import('./routes/window/404').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/gdpr' getComponent={() => import('./routes/gdpr').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/terms' getComponent={() => import('./routes/termsOfSale').then(module => module.default)} loading={() => <div>loading...</div>} />

                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/' getComponent={() => import('./routes/frontpage').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/home' view='homepage' getComponent={() => import('./routes/frontpage').then(module => module.default)} loading={() => <div>loading...</div>} />
                            <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} path='/home-wall' getComponent={() => import('./routes/frontpage').then(module => module.default)} loading={() => <div>loading...</div>} />
                            {otherRoutes.map(route => (
                                <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} {...(route.props || {})} key={`other-${route.path}`} path={route.path} getComponent={route.getComponent} loading={() => <div>loading...</div>} />
                            ))}
                            {signingRoutes.map(route => (
                                <AsyncRoute stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} {...(route.props || {})} key={`signing-${route.path}`} path={route.path} getComponent={route.getComponent} loading={() => <div>loading...</div>} />
                            ))}

                            <LoadingSpinner stores={this.stores} {...this.props} {...inputProps} scrolledDown={scrolledDownPlaceholder} default />

                            {/* <LoadingSpinner default goto='/' gotoTitle='Home' routerName='livecenter' /> */}
                        </Router>
                            </div>
                        </div>

                        <Match>
                            {({ matches, path, url }) => <>
                                {/^\/(welcome|login|register|forgotten|waiting-approval|window|calendarBooking|prescriptionOrder|onBoarding)/.test(url) ? <>
                                </> : <>
                                    {componentsOther.ArcadeTrigger && (
                                        <componentsOther.ArcadeTrigger
                                            key='arcade-trigger'
                                            stores={this.stores}
                                        />
                                    )}
                                    <Footer stores={this.stores} {...this.props} scrolledDown={scrolledDownPlaceholder} key={currentUrl} currentUrl={currentUrl} isCordova={isCordova} />
                                    <GdprBanner locale='nb' onAfterConsent={(c) => { /* optional hook */ }} />
                                    <QuickSearch ref={ref => this.quickSearchRef = ref} stores={this.stores} {...this.props} />
                                </>}
                            </>}
                        </Match>
                    </div>
                </Catcher>
            </IntlProvider>
        );
    }
}

export default App;
